import { Link } from "react-router-dom";
import { BrontoWordmark } from "@/components/BrontoLogo";
import { Aurora } from "@/components/Decor";
import { Seo } from "@/lib/useSeo";

/**
 * Legal pages — Terms, Privacy, Community Guidelines.
 *
 * Each section is expressed as a list of blocks. A block can be:
 *   { h: "Heading" }           — a section heading
 *   { p: "Paragraph text" }    — a paragraph
 *   { bullets: [ "…", "…" ] }  — a bullet list
 *   { rule: true }             — a subtle horizontal divider
 *
 * This structure lets us render the full Privacy Policy prose exactly
 * as authored, including all the sub-sections and bullet lists, without
 * forcing every block into the previous `{h, p}` mold.
 */
const sections = {
  terms: {
    title: "Terms of Service",
    intro:
      "By using BrontoY, you agree to the following terms. This page reflects general expectations; the final version will be published before public release.",
    blocks: [
      { h: "1. Your account" },
      { p: "You are responsible for maintaining the security of your account and the actions taken from it. You must be at least 13 years old to use BrontoY." },
      { h: "2. Content ownership" },
      { p: "You retain rights to the content you post. You grant BrontoY a licence to host, display and distribute your content within the service." },
      { h: "3. Community standards" },
      { p: "You agree to follow the Community Guidelines. Content that violates them may be removed, and accounts may be suspended." },
      { h: "4. Termination" },
      { p: "You may deactivate your account at any time. We may suspend or terminate accounts that violate these terms or the Guidelines." },
    ],
  },

  privacy: {
    title: "Privacy Policy",
    subtitle: "Privacy Policy for BrontoY",
    lastUpdated: "Last Updated: July 7, 2026",
    intro:
      "Welcome to BrontoY (\u201CBrontoY,\u201D \u201Cwe,\u201D \u201Cour,\u201D or \u201Cus\u201D).",
    blocks: [
      {
        p: "Your privacy is fundamental to us. This Privacy Policy describes how we collect, receive, access, store, process, disclose, transfer, protect, and retain information when you access or use BrontoY, including our mobile application, website, social features, profile features, posting tools, messaging features, and related services (collectively, the \u201CService\u201D).",
      },
      {
        p: "By accessing or using BrontoY, you acknowledge that you have read, understood, and agreed to the practices described in this Privacy Policy.",
      },
      { rule: true },

      { h: "Information We Collect" },
      {
        p: "We collect information to operate, secure, personalize, improve, and lawfully maintain the Service.",
      },

      { h: "a. Information You Provide Directly", sub: true },
      { p: "You may voluntarily provide information when creating or using your BrontoY account, including but not limited to:" },
      {
        bullets: [
          "Name, display name, or username",
          "Email address or login information",
          "Profile photo",
          "Bio",
          "Location information provided during onboarding or profile setup",
          "Date of birth",
          "Gender",
          "Pronouns",
          "Account preferences and profile details",
          "Posts, captions, comments, replies, likes, shares, and other interactions",
          "Opinion posts and different views shared by users",
          "Reports, appeals, feedback, and support requests",
        ],
      },
      {
        p: "You are responsible for ensuring that the information you provide is accurate and that you do not share personal or sensitive information publicly unless you choose to do so.",
      },
      { rule: true },

      { h: "b. Information Collected Automatically", sub: true },
      { p: "When you access or use the Service, we may automatically collect technical and operational information, including:" },
      {
        bullets: [
          "Device identifiers and characteristics",
          "Operating system, platform, and application version",
          "Internet Protocol address",
          "Approximate geographic location based on IP address",
          "Access times, usage logs, session activity, and interaction patterns",
          "Content views, searches, follows, likes, comments, shares, and messaging activity",
          "Crash reports, diagnostics, and performance data",
          "Security, fraud-prevention, and abuse-detection information",
        ],
      },
      {
        p: "This information is collected to ensure reliability, security, personalization, moderation, fraud prevention, and continuous improvement of BrontoY.",
      },
      { rule: true },

      { h: "c. Standard Posts, Opinion Posts & Social Interaction Data", sub: true },
      { p: "To provide BrontoY\u2019s social features, we process content and interaction data, including:" },
      {
        bullets: [
          "Standard posts, which include one image and a caption",
          "Opinion posts, which may include typed opinions without an image",
          "Different views shared by users in response to opinion posts",
          "Likes, comments, shares, follows, and profile interactions",
          "Public discussions and engagement activity",
          "Message activity and direct communications",
          "Reports, moderation actions, and appeal information",
        ],
      },
      {
        p: "Standard posts require one image and a caption. Opinion posts do not require an image and allow users to express an opinion, agree with others, or share a different view within the limits provided by the platform.",
      },
      {
        p: "Content you post publicly may be visible to other users and may be copied, screenshotted, shared, saved, or discussed by others. BrontoY cannot control how other users use information you choose to make public.",
      },
      {
        p: "Direct messages are not publicly visible, but recipients may save, copy, screenshot, forward, or report them. Unless expressly stated otherwise, BrontoY direct messages are not end-to-end encrypted.",
      },
      { rule: true },

      { h: "How We Use Your Information" },
      { p: "We process information for legitimate and lawful purposes, including but not limited to:" },
      {
        bullets: [
          "Operating, maintaining, and delivering BrontoY services",
          "Creating and managing user accounts",
          "Completing profile onboarding and displaying profile information",
          "Authenticating users and securing login sessions",
          "Displaying profiles, posts, captions, comments, messages, and interactions",
          "Enabling standard posts, opinion posts, comments, different views, likes, shares, follows, and messaging",
          "Delivering social feeds, search results, recommendations, and notifications",
          "Personalizing content, ranking feeds, and improving user experience",
          "Detecting, preventing, and mitigating spam, fraud, abuse, harassment, impersonation, and unauthorized activity",
          "Reviewing reports, enforcing platform rules, and moderating content",
          "Providing customer support and responding to inquiries",
          "Conducting analytics, audits, testing, and internal research",
          "Monitoring system integrity, performance, and reliability",
          "Enforcing our Terms & Conditions, Community Guidelines, and acceptable use policies",
          "Complying with applicable laws, regulations, court orders, subpoenas, and lawful requests",
        ],
      },
      { p: "BrontoY does not currently offer subscriptions, paid plans, or in-app purchases." },
      { rule: true },

      { h: "Public Visibility, User Content & Automated Processing" },
      { p: "BrontoY is a social platform. Users acknowledge that certain information may be visible to others depending on account settings and product features." },
      {
        bullets: [
          "Profile details may be visible to other users depending on your settings",
          "Public posts, captions, comments, opinion posts, different views, and interactions may be visible to other users",
          "Standard posts include an image and caption and may be viewed, liked, commented on, or shared by others",
          "Opinion posts may allow users to agree or share a different view",
          "Private settings may limit visibility but cannot prevent screenshots, copying, saving, or external sharing by approved viewers",
          "Direct messages are intended for selected recipients but may be reported, copied, saved, or disclosed by recipients",
          "BrontoY may use automated systems to rank feeds, recommend content, detect spam, identify unsafe activity, and support moderation",
          "Automated systems may not always be accurate and may be reviewed or adjusted where appropriate",
        ],
      },
      { p: "BrontoY does not sell user content, profile information, direct messages, or personal data." },
      { p: "Users are responsible for the content they choose to post, share, send, or disclose through the Service." },
      { rule: true },

      { h: "Data Sharing & Third-Party Services" },
      { p: "We may disclose limited information to third-party service providers strictly as necessary to operate, secure, and improve the Service, including:" },
      {
        bullets: [
          "Cloud infrastructure and hosting providers",
          "Database, storage, and media-processing providers",
          "Email, OTP, and notification delivery providers",
          "Authentication services, if enabled",
          "Analytics, diagnostics, and monitoring services",
          "Content moderation, safety, and abuse-prevention services",
          "Customer support and communication platforms",
          "Legal, compliance, and professional service providers",
        ],
      },
      { p: "All third parties are expected to process data securely, confidentially, and only for authorized purposes." },
      { p: "We do not sell, license, or commercially exploit personal data." },
      { p: "We may disclose information if required to comply with applicable law, legal process, government request, enforce our policies, protect user safety, prevent fraud, or defend the rights and security of BrontoY, users, or others." },
      { rule: true },

      { h: "Data Security" },
      { p: "We implement reasonable and appropriate administrative, technical, and organizational safeguards, including:" },
      {
        bullets: [
          "Encryption of data in transit",
          "Secure password handling",
          "Restricted access controls",
          "Secure infrastructure and hosting environments",
          "Monitoring for unauthorized, fraudulent, or anomalous activity",
          "Abuse-prevention and account-protection systems",
          "Limited internal access to personal data based on operational need",
        ],
      },
      { p: "Despite these safeguards, no method of transmission, processing, or storage is completely secure. BrontoY does not guarantee absolute security." },
      { p: "Users are responsible for maintaining the confidentiality of their login credentials and for activity occurring through their accounts." },
      { rule: true },

      { h: "Data Retention & Account Deletion" },
      { p: "We retain information only for as long as reasonably necessary to:" },
      {
        bullets: [
          "Provide, maintain, and improve the Service",
          "Keep accounts active and functional",
          "Maintain safety, security, and abuse-prevention systems",
          "Meet operational, legal, regulatory, and compliance obligations",
          "Resolve disputes and enforce agreements",
        ],
      },
      { p: "You may request permanent deletion of your BrontoY account through the platform." },
      { p: "Permanent account deletion requests are generally processed within 48 hours." },
      { p: "After deletion is processed:" },
      {
        bullets: [
          "Your account will be permanently removed from the platform",
          "Your profile will no longer be accessible through normal use of the Service",
          "Your posts and account-linked content may be deleted or de-identified",
          "Some information may remain for a limited period in backups, logs, legal records, abuse-prevention systems, or where retention is required by law",
          "Content already copied, screenshotted, shared, saved, or downloaded by other users may remain outside BrontoY\u2019s control",
        ],
      },
      { p: "Deleted information may remain temporarily in backup systems until those backups are overwritten or securely deleted." },
      { rule: true },

      { h: "Your Rights & Choices" },
      { p: "Depending on your jurisdiction, you may have rights to:" },
      {
        bullets: [
          "Request access to personal data",
          "Request correction of inaccurate data",
          "Request deletion of personal data",
          "Object to or restrict processing activities",
          "Withdraw consent where processing is based on consent",
          "Request a copy of certain data",
          "Manage profile information, account visibility, notifications, and permissions",
          "Delete posts, comments, messages, or your account where available",
        ],
      },
      { p: "Requests may be submitted through BrontoY settings or via the contact information below and will be addressed in accordance with applicable law." },
      { p: "We may require reasonable verification before processing privacy or account-deletion requests." },
      { rule: true },

      { h: "Children\u2019s Privacy" },
      { p: "BrontoY is not intended for individuals under the age of 13." },
      { p: "We do not knowingly collect personal data from children under 13. If such data is identified, it will be deleted promptly." },
      { p: "BrontoY currently does not provide parental controls." },
      { p: "Where local law requires a higher minimum age, parental consent, or additional protections for minors, BrontoY may restrict access, request additional verification, disable certain features, or apply additional safeguards." },
      { p: "Users are responsible for providing accurate age information when creating an account." },
      { rule: true },

      { h: "International Data Transfers" },
      { p: "Your information may be stored or processed in jurisdictions outside your country of residence." },
      { p: "By using the Service, you acknowledge that information may be transferred, stored, and processed across borders in accordance with this Privacy Policy and applicable law." },
      { p: "Where required, we use appropriate safeguards for international data transfers." },
      { rule: true },

      { h: "Changes to This Privacy Policy" },
      { p: "We may revise this Privacy Policy at any time." },
      { p: "Updates will be reflected by a revised \u201CLast Updated\u201D date. Material changes may be notified through the Service, email, or other appropriate means." },
      { p: "Continued use of the Service after changes become effective constitutes acceptance of the revised Privacy Policy." },
      { rule: true },

      { h: "Contact Us" },
      { p: "For privacy-related questions, requests, complaints, account-deletion requests, or concerns, please contact:" },
      { p: "BrontoY" },
      { p: "Email: privacy@brontoplatforms.com" },
      { p: "For urgent safety, impersonation, abuse, unlawful-content concerns, or account-related issues, users may contact us through the in-app reporting tools or the contact information above." },
    ],
  },

  guidelines: {
    title: "Community Guidelines",
    subtitle: "BrontoY Community Guidelines",
    lastUpdated: "Last Updated: July 14, 2026",
    intro:
      "Welcome to BrontoY. Our mission is to create a safe, respectful, and engaging community where people can connect, share ideas, express opinions, and interact responsibly. By using BrontoY, you agree to follow these Community Guidelines.",
    blocks: [
      {
        p: "Failure to comply with these Community Guidelines may result in content removal, account restrictions, suspension, or permanent termination of your account.",
      },
      { rule: true },

      { h: "1. Be Respectful" },
      { p: "Treat everyone with kindness, courtesy, and respect." },
      { p: "Do not:" },
      {
        bullets: [
          "Harass, threaten, or intimidate others.",
          "Bully or abuse other users.",
          "Encourage violence or hatred.",
          "Repeatedly target or demean individuals.",
        ],
      },
      { p: "Respectful discussion and constructive disagreement are encouraged." },
      { rule: true },

      { h: "2. No Hate Speech" },
      {
        p: "BrontoY does not permit content that promotes, encourages, or glorifies hatred, discrimination, or violence against individuals or groups based on protected characteristics, including race, ethnicity, nationality, religion, disability, sex, gender identity, or sexual orientation.",
      },
      { rule: true },

      { h: "3. No Illegal Activities" },
      { p: "Do not post or promote content that:" },
      {
        bullets: [
          "Violates applicable laws.",
          "Encourages criminal activity.",
          "Facilitates fraud or scams.",
          "Infringes intellectual property rights.",
          "Promotes illegal goods or services.",
        ],
      },
      { rule: true },

      { h: "4. Keep Content Appropriate" },
      { p: "The following content is prohibited:" },
      {
        bullets: [
          "Sexually explicit content.",
          "Child sexual abuse or exploitation.",
          "Graphic violence or gore.",
          "Content promoting or encouraging self-harm or suicide.",
          "Obscene or extremely disturbing material.",
        ],
      },
      { p: "Educational, artistic, documentary, or newsworthy content may be permitted where appropriate." },
      { rule: true },

      { h: "5. No Spam or Manipulation" },
      { p: "Do not:" },
      {
        bullets: [
          "Post repetitive or misleading content.",
          "Send spam.",
          "Artificially manipulate engagement.",
          "Use automated systems to abuse the platform.",
          "Share deceptive, malicious, or harmful links.",
        ],
      },
      { rule: true },

      { h: "6. No Impersonation" },
      { p: "Do not impersonate:" },
      {
        bullets: [
          "Another individual.",
          "A business or organization.",
          "Public figures.",
          "BrontoY employees or representatives.",
        ],
      },
      { p: "Parody or fan accounts must not mislead users into believing they represent another person or organization." },
      { rule: true },

      { h: "7. Protect Privacy" },
      { p: "Respect the privacy of others." },
      { p: "Do not share another person\u2019s:" },
      {
        bullets: [
          "Personal contact information.",
          "Government identification details.",
          "Financial information.",
          "Passwords or account credentials.",
          "Home address or other sensitive personal information without permission.",
        ],
      },
      { rule: true },

      { h: "8. Authentic Use" },
      { p: "Use BrontoY honestly and responsibly." },
      { p: "Do not:" },
      {
        bullets: [
          "Create accounts to evade enforcement actions.",
          "Use fake accounts for abuse or deception.",
          "Attempt to bypass platform safety or moderation systems.",
        ],
      },
      { rule: true },

      { h: "9. Opinion Posts" },
      { p: "BrontoY encourages thoughtful conversations and respectful debate." },
      { p: "When sharing opinions:" },
      {
        bullets: [
          "Focus on ideas rather than attacking individuals.",
          "Respect differing viewpoints.",
          "Avoid abusive, hateful, or threatening language.",
          "Help create meaningful and constructive discussions.",
        ],
      },
      { rule: true },

      { h: "10. Reporting Violations" },
      { p: "If you believe content violates these Community Guidelines, please use the in-app Report feature." },
      { p: "Our moderation team reviews reported content and takes appropriate action where necessary." },
      { p: "Knowingly submitting false or malicious reports may itself violate these Community Guidelines." },
      { rule: true },

      { h: "11. Enforcement" },
      { p: "When content or behaviour violates these Community Guidelines, BrontoY may:" },
      {
        bullets: [
          "Remove content.",
          "Restrict certain account features.",
          "Issue warnings.",
          "Temporarily suspend accounts.",
          "Permanently terminate accounts.",
          "Cooperate with lawful requests from competent authorities where required by applicable law.",
        ],
      },
      { p: "Repeated or serious violations may result in permanent removal from the platform." },
      { rule: true },

      { h: "12. Automated Moderation" },
      {
        p: "BrontoY uses automated moderation technologies to help identify content that may violate these Community Guidelines, including abusive, harmful, explicit, misleading, spam, or otherwise prohibited content.",
      },
      {
        p: "Content may be reviewed automatically before or after publication to help maintain a safe and respectful community. Automated moderation assists our moderation process and may be supplemented by manual review where appropriate.",
      },
      {
        p: "Users who repeatedly violate these Community Guidelines may have their content removed or their accounts restricted, suspended, or permanently terminated.",
      },
      { rule: true },

      { h: "13. Minimum Age" },
      { p: "BrontoY is intended only for users who meet our minimum age requirements." },
      { p: "Users who do not meet the minimum age requirement are not permitted to create or maintain an account." },
      { p: "Accounts found to violate this requirement may be removed." },
      { rule: true },

      { h: "14. Appeals" },
      {
        p: "If you believe your content or account was moderated incorrectly, you may contact BrontoY Support through the app or our official support channels for review.",
      },
      { p: "We will review eligible appeals in accordance with our moderation procedures." },
      { rule: true },

      { h: "15. Changes to These Guidelines" },
      {
        p: "BrontoY may update these Community Guidelines from time to time to improve user safety, comply with legal requirements, or reflect changes to our services.",
      },
      { p: "Continued use of BrontoY after updates constitutes acceptance of the revised Community Guidelines." },
      { rule: true },

      { h: "Contact Us" },
      {
        p: "If you have any questions about these Community Guidelines or wish to report concerns, please contact BrontoY through the in-app Support feature or by email at support@brontoplatforms.com. Additional contact information is available on our official website.",
      },
    ],
  },
};

export default function LegalPage({ variant = "terms" }) {
  const s = sections[variant];
  const seoByVariant = {
    terms: {
      title: "Terms of Use — BrontoY",
      description:
        "Read the BrontoY Terms of Use governing your access to and use of the BrontoY social platform by Bronto Intelligence.",
      canonicalPath: "/terms",
    },
    privacy: {
      title: "Privacy Policy — BrontoY",
      description:
        "Read the BrontoY Privacy Policy: how Bronto Intelligence collects, uses, stores and protects your data on the BrontoY social platform.",
      canonicalPath: "/privacy",
    },
    guidelines: {
      title: "Community Guidelines — BrontoY",
      description:
        "The BrontoY Community Guidelines: rules for respectful, safe and constructive participation on the BrontoY social platform.",
      canonicalPath: "/guidelines",
    },
  };
  const seo = seoByVariant[variant] || seoByVariant.terms;
  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-hidden">
      <Seo title={seo.title} description={seo.description} canonicalPath={seo.canonicalPath} />
      <Aurora variant="subtle" />
      <nav className="relative z-10 border-b border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <BrontoWordmark size="text-2xl" />
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <Link to="/terms" className="hover:text-foreground" data-testid="legal-terms-link">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground" data-testid="legal-privacy-link">Privacy</Link>
            <Link to="/guidelines" className="hover:text-foreground" data-testid="legal-guidelines-link">Guidelines</Link>
          </div>
        </div>
      </nav>
      <div className="relative z-10 max-w-3xl mx-auto px-6 py-16">
        <h1
          className="font-display text-5xl md:text-6xl font-medium tracking-tighter leading-tight mb-4"
          data-testid={`legal-title-${variant}`}
        >
          {s.title}
        </h1>
        {s.subtitle && (
          <p className="font-display text-xl md:text-2xl text-foreground/80 mb-2">{s.subtitle}</p>
        )}
        {s.lastUpdated && (
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-8">{s.lastUpdated}</p>
        )}
        {s.intro && (
          <p className="text-lg text-muted-foreground leading-relaxed mb-10">{s.intro}</p>
        )}

        <div className="space-y-4">
          {s.blocks.map((b, i) => {
            if (b.rule) {
              return <div key={i} className="my-6 h-px bg-border/60" aria-hidden />;
            }
            if (b.h) {
              return (
                <h2
                  key={i}
                  className={
                    b.sub
                      ? "font-display text-xl font-medium tracking-tight mt-4 mb-1"
                      : "font-display text-2xl md:text-[26px] font-medium tracking-tight mt-6 mb-2"
                  }
                >
                  {b.h}
                </h2>
              );
            }
            if (b.bullets) {
              return (
                <ul
                  key={i}
                  className="list-disc pl-6 space-y-1.5 text-base text-muted-foreground leading-relaxed marker:text-muted-foreground/60"
                >
                  {b.bullets.map((x, j) => (
                    <li key={j}>{x}</li>
                  ))}
                </ul>
              );
            }
            return (
              <p key={i} className="text-base text-muted-foreground leading-relaxed">
                {b.p}
              </p>
            );
          })}
        </div>

        <p
          className="mt-16 pt-8 border-t border-border/60 text-[11px] uppercase tracking-[0.24em] text-muted-foreground text-center"
          data-testid="legal-page-eyebrow"
        >
          BrontoY — By Bronto Intelligence
        </p>
      </div>
    </div>
  );
}
